// new code - merged with old functionality
import { useState } from "react";
import { isEmpty } from "lodash";
import dayjs from "dayjs";
import { AiFillEye } from "react-icons/ai";
import { HiChevronDown } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import { BsArrowUpShort } from "react-icons/bs";
import { apiGet, apiPut, apiDelete } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import useToastContext from "hooks/useToastContext";
import StartAIInterview from "./StartAIInterview";
import ScheduleZoomInterview from "./ScheduleZoomInterview"; // Imported old component
import RejectReply from "./RejectReply";
import ReportView from "./ReportView";
import StatusDropdown from "./StatusDropdown";
import InterviewAssignForms from "./InterviewAssignForms";
import InterviewTypeModal from "./InterviewTypeModal";
import ConfirmationModal from "components/reusable/ConfirmationModal";

const LINK_ALREADY_SENT = ["scheduled", "rescheduled"];

const Table = ({
  requestList,
  handleRequestView,
  allRequest,
  page,
  setPage,
  sort,
  setSort,
}) => {
  const { t } = useTranslation();
  const notification = useToastContext();

  // Existing States
  const [currentItem, setCurrentItem] = useState("");
  const [rejectItem, setRejectItem] = useState("");
  const [jobRequest, setJobRequest] = useState(""); // drives StartAIInterview modal
  const [zoomJobRequest, setZoomJobRequest] = useState(""); // drives ScheduleZoomInterview modal
  const [jobApply, setJobApply] = useState({});
  const [replyShowModal, setReplyShowModal] = useState(false);
  const [reportSessionId, setReportSessionId] = useState(null); // drives ReportView modal

  // NEW STATES FOR ASSIGN FORMS
  const [assignFormModal, setAssignFormModal] = useState(false);
  const [currentAssignItem, setCurrentAssignItem] = useState(null);

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetItem, setResetItem] = useState(null);
  const [isResetting, setIsResetting] = useState(false);

  // NEW: item that triggered the "Select Interview Type" popup (AI vs Zoom)
  const [interviewModalItem, setInterviewModalItem] = useState(null);

  // Old Handlers for AI and Zoom
  const handleStartAIInterview = (item) => {
    setCurrentItem(item);
    setJobRequest(true); // Old code used setAiJobRequest(true)
    setZoomJobRequest(false);
    setJobApply({ ...item });
  };

  const handleScheduleZoomInterview = (item) => {
    setCurrentItem(item);
    setZoomJobRequest(true); // Old code used setZoomJobRequest(true)
    setJobRequest(false);
    setJobApply({ ...item });
  };

  const handleOpenReport = async (item) => {
    if (item?.aiInterviewSessionID) {
      setReportSessionId(item.aiInterviewSessionID);
      return;
    }

    try {
      const res = await apiGet(
        `${apiPath.aiInterviewSessionByJobRequest}/${item._id}`,
        {},
      );
      if (res?.data?.success && res?.data?.results?.sessionId) {
        setReportSessionId(res.data.results.sessionId);
      } else {
        notification.error("No interview session found for this request.");
      }
    } catch (err) {
      notification.error("Failed to fetch interview session.");
    }
  };

  const handleSelectRejected = (item) => {
    setRejectItem(item);
    setReplyShowModal(true);
    setJobApply({ _id: item.Employee?._id });
  };

  const handleInterviewStatusChange = async (item, status) => {
    if (status === "accepted") {
      setCurrentAssignItem(item);
      setAssignFormModal(true);
      return;
    }

    try {
      const payload = { interviewStatus: status };
      const path = `${apiPath.ChangeInterviewStatus}/${item?._id}`;
      const result = await apiPut(path, payload);

      if (result?.status === 200) {
        notification.success(result.data.message);
        allRequest();
      } else {
        notification.error(result?.data?.message);
      }
    } catch (error) {
      console.error("Error changing interview status:", error.message);
      notification.error("Failed to update interview status");
    }
  };

  const changeStatus = async (item) => {
    try {
      const payload = {
        status: item?.status,
        employeeID: item?._id,
        companyID: item?.companyID,
      };
      const path = `${apiPath.ChangeStatusJob}/${item?._id}`;
      const result = await apiPut(path, payload);
      if (result?.status === 200) {
        notification.success(result.data.message);
        allRequest();
      } else {
        notification.error(result?.data?.message);
      }
    } catch (error) {
      console.error("error in get all sub admin list==>>>>", error.message);
    }
  };

  // Old Reset Logic (handles AI and Zoom)
  const handleResetInterview = async () => {
    if (!resetItem) return;
    
    setIsResetting(true);
    try {
      let endpoint;
      let message;
      
      // Determine which reset endpoint to use based on interview type
      if (resetItem.interviewType === 'ai' && resetItem.aiInterviewSessionID) {
        // AI Interview reset
        endpoint = `${apiPath.aiInterviewReset}/${resetItem.aiInterviewSessionID}`;
        message = "AI Interview reset successfully";
      } else if (resetItem.interviewType === 'zoom') {
        // Zoom Interview reset
        endpoint = `${apiPath.zoomInterviewReset}/${resetItem._id}`;
        message = "Zoom Interview reset successfully";
      } else {
        notification.error("Unable to determine interview type");
        setIsResetting(false);
        return;
      }

      const res = await apiDelete(endpoint);
      if (res?.status === 200) {
        notification.success(message);
        allRequest();
        setResetModalOpen(false);
        setResetItem(null);
      } else {
        notification.error(res?.data?.message || "Failed to reset");
      }
    } catch (err) {
      notification.error("Failed to reset interview");
    } finally {
      setIsResetting(false);
    }
  };

  // Whether the currently-open interview-type modal is for a reschedule
  const interviewModalIsReschedule = interviewModalItem
    ? LINK_ALREADY_SENT.includes(
        interviewModalItem?.interviewStatus || "pending",
      )
    : false;

  return (
    <div className="p-3">
      <div className="overflow-x-auto relative rounded-lg border">
        <table className="w-full text-xs text-left text-[#A5A5A5] dark:text-gray-400 ">
          <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="py-3 px-6">
                Sr no.
              </th>
              <th scope="col" className="py-3 px-6">
                {t("EMPLOYEE_NAME")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("JOB_TITLE")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("DEPARTMENT")}
              </th>
              <th scope="col" className="py-3 px-6">
                {t("YEAR_OF_EXPERIENCE")}
              </th>
              <th
                scope="col"
                className="py-3 px-6 cursor-pointer"
                onClick={() => {
                  if (sort.sortBy === "createdAt" && sort.sortType === "asc") {
                    setSort({ sortBy: "createdAt", sortType: "desc" });
                  } else {
                    setSort({ sortBy: "createdAt", sortType: "asc" });
                  }
                }}
              >
                <div className="flex justify-start">
                  <span>{t("JOB_APPLIED_DATE")}</span>
                  <span>
                    {sort.sortBy === "createdAt" && sort.sortType === "asc" && (
                      <BsArrowUpShort className="w-4 h-4" />
                    )}
                    {sort.sortBy === "createdAt" &&
                      sort.sortType === "desc" && (
                        <BsArrowUpShort className="w-4 h-4 rotate-180" />
                      )}
                  </span>
                </div>
              </th>
              <th scope="col" className="py-3 px-6">
                {t("INTER_STATUS")}
              </th>
              <th scope="col" className="py-3 px-6 text-center">
                {t("O_ACTION")}
              </th>
            </tr>
          </thead>
          <tbody>
            {requestList &&
              requestList?.map((item, i) => {
                const status = item?.interviewStatus || "pending";
                const isLocked = status === "accepted" || status === "rejected";
                const isReschedule = LINK_ALREADY_SENT.includes(status);

                return (
                  <tr
                    key={i}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                  >
                    <th
                      scope="row"
                      className="py-4 px-6 border-r font-medium text-gray-900 dark:text-white"
                    >
                      {i + 1 + 10 * (page - 1)}
                    </th>
                    <td className="py-4 px-6 border-r text-gray-900 text-left">
                      {item?.Employee?.name}
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900 text-left">
                      {item?.Job?.name}
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900 text-left">
                      {item?.JobDepartment?.name}
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900 text-left">
                      {item?.yearOfExperiece}
                    </td>
                    <td className="py-4 px-6 border-r text-gray-900 text-left">
                      {dayjs(item?.createdAt).format("MM-DD-YYYY hh:mm A")}
                    </td>

                    {/* Status Column */}
                    <td className="py-4 px-6 border-r text-gray-900 text-left">
                      {status === "accepted" || status === "rejected" ? (
                        <span className="capitalize text-xs font-medium">
                          {status}
                        </span>
                      ) : (
                        <StatusDropdown
                          currentStatus={status}
                          onChangeStatus={(next) =>
                            handleInterviewStatusChange(item, next)
                          }
                          onSelectRejected={() => handleSelectRejected(item)}
                        />
                      )}
                    </td>

                    {/* Action Column */}
                    <td className="py-4 px-6 border-l text-gray-900">
                      <ul className="items-center justify-center flex gap-1">
                        <li
                          className="hover:text-LightBlue hover:bg-white"
                          onClick={() => handleRequestView(item)}
                        >
                          <a title={t("O_VIEW_USER")}>
                            <AiFillEye className="w-5 h-5 cursor-pointer text-slate-600" />
                          </a>
                        </li>

                        {/* Single "Interview" button — opens InterviewTypeModal popup (AI / Zoom) */}
                        {!isLocked &&
                          (status === "pending" || isReschedule) && (
                            <li>
                              <button
                                onClick={() => setInterviewModalItem(item)}
                                className={`px-3 py-1 text-white rounded flex items-center gap-1 ${
                                  isReschedule
                                    ? "bg-yellow-500 hover:bg-yellow-600"
                                    : "bg-green-500 hover:bg-green-600"
                                }`}
                                title={
                                  isReschedule
                                    ? "Reschedule Interview"
                                    : "Start Interview"
                                }
                              >
                                {isReschedule ? "Reschedule" : "Interview"}{" "}
                                <HiChevronDown className="w-3 h-3" />
                              </button>
                            </li>
                          )}

                        {status === "completed" && (
                          <li>
                            <button
                              onClick={() => handleOpenReport(item)}
                              disabled={
                                !item?.aiInterviewSessionID && !item?._id
                              }
                              className="px-3 py-1 bg-LightBlue text-white rounded hover:bg-DarkBlue disabled:opacity-50"
                              title="View Report"
                            >
                              Report
                            </button>
                          </li>
                        )}

                        {/* DEV RESET BUTTON - ONLY SHOWS IN DEVELOPMENT */}
                        {process.env.NODE_ENV === "development" && (
                          <li>
                            <button
                              onClick={() => {
                                setResetItem(item);
                                setResetModalOpen(true);
                              }}
                              className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                              title="Dev Only: Reset Interview"
                            >
                              Reset
                            </button>
                          </li>
                        )}
                      </ul>
                    </td>
                  </tr>
                );
              })}
            {isEmpty(requestList) ? (
              <tr className="bg-white text-center border-b dark:bg-gray-800 dark:border-gray-700">
                <td className="py-4 px-6 border-r" colSpan={8}>
                  {t("O_NO_RECORD_FOUND")}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Existing Modals */}
      <>
        {jobRequest ? (
          <StartAIInterview
            setJobRequest={setJobRequest}
            currentItem={currentItem}
            changeStatus={changeStatus}
            allRequest={allRequest}
            setPage={setPage}
            jobApply={jobApply}
          />
        ) : null}
      </>

      {/* Zoom Modal - Old Functionality */}
      <>
        {zoomJobRequest ? (
          <ScheduleZoomInterview
            setJobRequest={setZoomJobRequest}
            currentItem={currentItem}
            changeStatus={changeStatus}
            allRequest={allRequest}
            setPage={setPage}
            jobApply={jobApply}
          />
        ) : null}
      </>

      {replyShowModal ? (
        <RejectReply
          setReplyShowModal={setReplyShowModal}
          rejectItem={rejectItem}
          changeStatus={changeStatus}
          allRequest={allRequest}
          setPage={setPage}
          jobApply={jobApply}
        />
      ) : null}
      {reportSessionId ? (
        <ReportView
          sessionId={reportSessionId}
          onClose={() => setReportSessionId(null)}
        />
      ) : null}

      {/* Assign Forms Popup */}
      {assignFormModal && currentAssignItem && (
        <InterviewAssignForms
          setAssignFormModal={setAssignFormModal}
          currentItem={currentAssignItem}
          allRequest={allRequest}
          setPage={setPage}
          jobApply={{
            _id: currentAssignItem?.Employee?._id,
            ...currentAssignItem,
          }}
        />
      )}

      {/* NEW: "Select Interview Type" popup — choose AI Interview or Zoom Interview */}
      <InterviewTypeModal
        isOpen={!!interviewModalItem}
        onClose={() => setInterviewModalItem(null)}
        employeeName={interviewModalItem?.Employee?.name}
        isReschedule={interviewModalIsReschedule}
        currentInterviewType={interviewModalItem?.interviewType} // Your existing logic
        currentItem={interviewModalItem} // ⬅️ REQUIRED FOR DETAILS TO SHOW
        onSelectAI={() => {
          const item = interviewModalItem;
          setInterviewModalItem(null);
          handleStartAIInterview(item);
        }}
        onSelectZoom={() => {
          const item = interviewModalItem;
          setInterviewModalItem(null);
          handleScheduleZoomInterview(item); // Old function called here
        }}
      />

      <ConfirmationModal
        isOpen={resetModalOpen}
        onClose={() => setResetModalOpen(false)}
        onConfirm={handleResetInterview}
        title="Reset Interview"
        message="This will delete ALL interview data for this candidate (questions, answers, report, recording, and session status). The candidate will receive a fresh interview link. Continue?"
        confirmText="Reset"
        cancelText="Cancel"
        isLoading={isResetting}
        isDanger={true}
      />
    </div>
  );
};

export default Table;